import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import mt1MaxMainImg from "@/assets/robots/mt1-max-hero.jpeg";
import mt1MaxGarageImg from "@/assets/robots/mt1-max-garage.jpeg";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
const MT1Max = () => {
  const specifications = [
    { label: "Product Size", value: "840×600×675 mm" },
    { label: "Trash Box Capacity", value: "35L" },
    { label: "Cleaning Function", value: "Sweeping" },
    { label: "Standard Runtime", value: "8 hours (Medium Mode)" },
    { label: "Default Speed", value: "0.8m/s" },
    { label: "Cleaning Efficiency", value: "1450m²/h at default speed" },
    { label: "Total Cleaning Area", value: "11,600m² per single charge" },
    { label: "Floor Area Cleaned", value: "29,000m² per single charge" },
    { label: "Coverage Area", value: "Multi-floor capability" },
    { label: "Best For", value: "Hotels & Hospitals" }
  ];

  const keyFeatures = [
    {
      title: "Exceptional Runtime",
      description: "Industry-leading 8-hour runtime in medium mode allows for extended cleaning operations without interruption. Perfect for large facilities that require continuous cleaning throughout the day."
    },
    {
      title: "Massive Coverage",
      description: "Clean up to 29,000m² of floor area on a single charge. With 11,600m² total cleaning area capacity, the MT1 Max handles the largest commercial and industrial spaces with ease."
    },
    {
      title: "Heavy-Duty Capacity",
      description: "Large 35L trash box capacity minimizes emptying frequency, making it ideal for high-traffic areas and large-scale cleaning operations in hotels, hospitals, and commercial buildings."
    },
    {
      title: "Multi-Floor Operation",
      description: "Advanced navigation system enables seamless multi-floor cleaning operations. Coordinate with elevators and navigate between floors automatically for comprehensive facility coverage."
    },
    {
      title: "AI-Powered Navigation",
      description: "Intelligent pathfinding and obstacle avoidance ensure efficient cleaning routes. Adapts to changing environments and optimizes cleaning patterns for maximum efficiency."
    },
    {
      title: "High-Speed Performance",
      description: "Operating at 0.8m/s default speed with 1450m²/h cleaning efficiency, the MT1 Max delivers professional-grade cleaning performance for time-sensitive operations."
    },
    {
      title: "Industrial-Grade Durability",
      description: "Built for demanding 24/7 commercial environments with robust construction and reliable components. Designed to withstand heavy daily use in the most challenging settings."
    },
    {
      title: "Smart Scheduling",
      description: "Automated scheduling and task management allow for optimized cleaning workflows. Set up recurring tasks and the robot will execute them independently."
    }
  ];

  const idealApplications = [
    {
      industry: "Hotels",
      description: "Perfect for cleaning lobbies, hallways, conference rooms, and back-of-house areas across multiple floors."
    },
    {
      industry: "Hospitals",
      description: "Maintains hygiene standards in corridors, waiting areas, and administrative spaces with minimal disruption to operations."
    },
    {
      industry: "Commercial Buildings",
      description: "Handles large office buildings, shopping centers, and public spaces with efficiency and reliability."
    },
    {
      industry: "Industrial Facilities",
      description: "Tackles warehouses, manufacturing floors, and distribution centers where traditional cleaning is challenging."
    }
  ];

  return (
    <div className="min-h-screen pt-12 pb-8 px-4 md:pt-16" style={{
      background: `radial-gradient(900px 600px at 10% 10%, #132552 0%, #0B1736 45%, #060f24 100%)`
    }}>
      <main className="max-w-7xl mx-auto">
        <Button asChild variant="ghost" className="mb-6">
          <Link to="/robots">
            <ArrowLeft className="mr-2 w-4 h-4" /> Back to Robots
          </Link>
        </Button>

        {/* Hero Section */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <div>
            <p className="text-primary font-semibold mb-2">Heavy-Duty Industrial Sweeper</p>
            <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-6xl mb-6 break-words">
              PUDU MT1 Max
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mb-6 leading-relaxed">
              The ultimate solution for large-scale cleaning operations. With an impressive 8-hour runtime and ability to clean up to 29,000m² on a single charge, the MT1 Max is engineered for the most demanding commercial and industrial environments.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Card className="glass-effect border-[hsl(var(--line))] p-4 text-center">
                <p className="text-3xl font-bold text-primary mb-1">8h</p>
                <p className="text-xs text-muted-foreground">Runtime</p>
              </Card>
              <Card className="glass-effect border-[hsl(var(--line))] p-4 text-center">
                <p className="text-3xl font-bold text-primary mb-1">29,000m²</p>
                <p className="text-xs text-muted-foreground">Floor Coverage</p>
              </Card>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" variant="secondary" className="glow-gold">
                <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                  Book a Demo
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                  Apply for Financing
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/accessories">
                  View Accessories
                </Link>
              </Button>
            </div>
          </div>
          <div className="rounded-lg overflow-hidden bg-white/5">
            <img src={mt1MaxMainImg} alt="PUDU MT1 Max" className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Real-World Deployment */}
        <section className="mb-12">
          <h2 className="font-orbitron font-bold text-3xl mb-6">Real-World Deployment</h2>
          <div className="rounded-lg overflow-hidden border border-[hsl(var(--line))]">
            <img src={mt1MaxGarageImg} alt="PUDU MT1 Max operating in a parking garage" className="w-full h-auto object-cover" />
          </div>
        </section>

        {/* Key Features */}
        <section className="mb-12">
          <h2 className="font-orbitron font-bold text-3xl mb-8">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {keyFeatures.map((feature, idx) => (
              <Card key={idx} className="glass-effect border-[hsl(var(--line))] p-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Ideal Applications */}
        <section className="mb-12">
          <h2 className="font-orbitron font-bold text-3xl mb-8">Ideal Applications</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {idealApplications.map((app, idx) => (
              <Card key={idx} className="glass-effect border-[hsl(var(--line))] p-6">
                <h3 className="font-semibold text-xl mb-3 text-primary">{app.industry}</h3>
                <p className="text-sm text-muted-foreground">{app.description}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Technical Specifications */}
        <section className="mb-12">
          <h2 className="font-orbitron font-bold text-3xl mb-8">Technical Specifications</h2>
          <Card className="glass-effect border-[hsl(var(--line))] p-6">
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
              {specifications.map((spec, idx) => (
                <div key={idx} className="border-b border-[hsl(var(--line))] pb-3">
                  <p className="text-sm text-muted-foreground mb-1">{spec.label}</p>
                  <p className="font-semibold">{spec.value}</p>
                </div>
              ))}
            </div>
          </Card>
        </section>

        {/* ROI Section */}
        <section className="mb-12">
          <Card className="glass-effect border-[hsl(var(--line))] p-8">
            <h2 className="font-orbitron font-bold text-2xl mb-6 text-center">
              Return on Investment
            </h2>
            <div className="grid md:grid-cols-4 gap-6 mb-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-primary mb-2">2-3</p>
                <p className="text-sm text-muted-foreground">Staff Replaced</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-primary mb-2">$62,400</p>
                <p className="text-sm text-muted-foreground">Annual Savings</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-primary mb-2">4.5 mo</p>
                <p className="text-sm text-muted-foreground">Payback Period</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-primary mb-2">260%</p>
                <p className="text-sm text-muted-foreground">Year 1 ROI</p>
              </div>
            </div>
            <p className="text-center text-muted-foreground mb-6">
              *ROI based on average labor costs. Every month after payback is pure profit.
            </p>
            <div className="text-center">
              <Button asChild size="lg">
                <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                  Calculate Your Savings
                </a>
              </Button>
            </div>
          </Card>
        </section>
      </main>

      </div>
  );
};

export default MT1Max;
